import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import Delete from "../../assets/svgs/Recycle Bin.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import { Skeleton } from "@chakra-ui/react";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import addContactIcon from "../../assets/svgs/add-contact.svg";
import { initialContactFilterData } from "../../utils/initialData";
import ContactArchiveFilter from "./ArchiveFilter/ContactArchiveFilter";
import DataAsOfNotice from "../../components/DataAsOfNotice";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const ContactArchive = () => {
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });
  const queryParams = new URLSearchParams(location.search);

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("contactArchiveFilterData");
    return localFilterData ? JSON.parse(localFilterData) : initialContactFilterData;
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  // URL sync with debounced search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const listIds = listData?.map((el) => el.id);

  const buildQueryParams = (filters = filterData) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;
    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { count, start_count, end_count } = filters;

    if (count !== "" && count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== "" && start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== "" && end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    localStorage.setItem("queryParams", params);

    return params;
  };

  const fetchData = (filters) => {
    const queryParams = buildQueryParams(filters);
    axios
      .get(`${BASE_URL}/archive-listing?${queryParams}&type=contact`, config)
      .then((res) => {
        const value = res?.data?.contactlist;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedId("");
      });
  };

  const onSuccess = (filters) => {
    setLoading(true);
    if (filters) {
      fetchData(filters);
    } else {
      fetchData(filterData);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search]);

  const handleSelectCheck = (id) => {
    if (selectedItem.length === 0) {
      setSelectedItem([id]);
    } else {
      const index = selectedItem?.indexOf(id);
      if (index === -1) {
        setSelectedItem([...selectedItem, id]);
      } else {
        const filterData = selectedItem?.filter((el) => el !== id);
        setSelectedItem(filterData);
      }
    }
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = () => {
    const dataToSend = {
      action: "delete",
      type: "contact",
      ids: [selectedId],
    };
    setDisableDelete(true);
    axios
      .post(`${BASE_URL}/handle-bulkaction`, dataToSend, config)
      .then((res) => {
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">
          Contact Archive
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
      </div>

      <div className="md:flex justify-between items-center mb-4 mt-2 px-1">
        <div className="flex gap-4 items-center flex-wrap">
          <ContactArchiveFilter
            filterData={filterData}
            from="contactarchive"
            onSetFilterData={(value) => {
              setFilterData(value);
            }}
            onCallApiAgain={(filters) => onSuccess(filters)}
          />
          {/* Updated Search Box */}
          <div className="search-box contacts relative w-[250px]">
            <input
              type="text"
              className="body-S w-full"
              placeholder="Search name"
              value={searchInput}
              onChange={handleInputChange}
              style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
            />
          </div>
        </div>
      </div>

      <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} bg-white rounded-xl border border-gray-200 overflow-hidden mt-4 relative min-h-[400px]`}>
        {loading && (
          <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
            <Loader />
          </div>
        )}
        <div className="table-info">
            <table className="contact-table two-rows-static">
              <thead>
                <tr className="uppercase body-N dark-M">
                  <th>
                    <label className="container">
                      <input
                        type="checkbox"
                        checked={listData?.length === 0 ? false : isSelectAll}
                        onChange={(e) => {
                          setIsSelectAll(e.target.checked);
                          if (e.target.checked) {
                            setSelectedItem(listIds);
                          } else {
                            setSelectedItem([]);
                          }
                        }}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </th>
                  <th className="green-H">
                    <div className="table-header">
                      First Name
                      <img
                        role="button"
                        src={sortBy !== "first_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("first_name");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Last Name
                      <img
                        role="button"
                        src={sortBy !== "last_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("last_name");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Date Archived
                      <img
                        src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        alt="icon"
                        onClick={() => {
                          setSortBy("updated_at");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">
                      Tags
                      <img
                        role="button"
                        src={sortBy !== "tags" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                        onClick={() => {
                          setSortBy("tags");
                          if (sortDirection === "desc") {
                            setSortDirection("asc");
                          } else {
                            setSortDirection("desc");
                          }
                        }}
                        alt="icon"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="table-header">Actions</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  [...Array(10)].map((_, idx) => (
                    <tr key={idx} className="dark-H body-N">
                      <td><Skeleton height="20px" width="20px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="100px" /></td>
                      <td><Skeleton height="20px" width="60px" /></td>
                    </tr>
                  ))
                ) : listData?.length === 0 ? (
                  <tr style={{ border: "none" }} className="border-none">
                    <td colSpan={6} className="body-N text-center mt-6 p-10 border-none" style={{ border: "none" }}>
                      <div className="flex flex-col items-center justify-center py-12">
                        <img src={addContactIcon} alt="No Contact Archives" className="w-16 h-16 mb-4 opacity-60" />
                        <p className="head-4 dark-H mb-2">No Contact Archives Found</p>
                        <p className="body-N dark-M mb-6">There are no archived contacts matching your search or filters.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  listData?.map((el, idx) => (
                    <tr key={idx} className="dark-H body-N">
                    <td className="">
                      <label className="container">
                        <input type="checkbox" checked={selectedItem?.includes(el.id)} onChange={() => handleSelectCheck(el?.id)} />
                        <span className="checkmark"></span>
                      </label>
                    </td>
                    <td className="capitalize">{el?.first_name} </td>
                    <td className="capitalize">{el?.last_name}</td>
                    <td>{moment(el?.updated_at).tz('America/New_York').format("MM/DD/YYYY hh:mm A")}</td>
                    <td style={{ maxWidth: "350px" }}>
                      <div className="flex flex-wrap items-center gap-1">
                        {el?.tags
                          ?.filter((el) => el !== "")
                          ?.map((tag, i) => (
                            <p key={i} className="tags green-H body-S">
                              {tag?.tag_name ? tag?.tag_name : tag}
                            </p>
                          ))}
                      </div>
                    </td>
                    <td className="flex content-center">
                      <img
                        className="mx-4 sidebar-icons"
                        src={Delete}
                        alt="delete"
                        role="button"
                        onClick={() => {
                          setSelectedId(el?.id);
                          setShowDeleteModal(true);
                        }}
                      />
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
        </div>

        <ContactPagination
          from="archive"
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
          paginationData={paginationData}
          handleNext={handleNext}
          handlePrev={handlePrev}
          onSuccess={onSuccess}
          itemPerPage={itemPerPage}
          handleItemPerPageSelect={(value) => {
            setItemPerPage(value);
            setCurrentPage(1);
          }}
        />

        <div className="px-6 pb-2">
          <DataAsOfNotice  />
        </div>
      </div>
      
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
    </BaseLayout>
  );
};

export default ContactArchive;